import type {
  CarnivalRngState,
  Die,
  DieId,
  LustPick,
  SinChoice,
  WrathTarget,
} from "../types";
import { getUnlockedBlackDice, nearestUnlockedDie, rerollDie } from "../utils";

function lockDice(
  dice: readonly Die[],
  dieIds: readonly DieId[],
  ownerId: string,
  sin: SinChoice["sin"],
): readonly Die[] {
  const targets = new Set(dieIds);
  return dice.map((die) =>
    targets.has(die.id) ? { ...die, lockedBy: { ownerId, sin } } : die,
  );
}

export interface ResolveSinResult {
  readonly rngState: CarnivalRngState;
  readonly dice: readonly Die[];
}

export function resolveGreed(
  dice: readonly Die[],
  playerId: string,
  dieIds: readonly [DieId, DieId] | null,
): readonly Die[] {
  return dieIds ? lockDice(dice, dieIds, playerId, "greed") : dice;
}

export function resolveGluttony(
  dice: readonly Die[],
  playerId: string,
  dieId: DieId | null,
): readonly Die[] {
  return dieId ? lockDice(dice, [dieId], playerId, "gluttony") : dice;
}

export function resolveLust(
  dice: readonly Die[],
  playerId: string,
  picks: readonly [LustPick, LustPick] | null,
): readonly Die[] {
  return picks
    ? lockDice(
        dice,
        picks.map((pick) => pick.dieId),
        playerId,
        "lust",
      )
    : dice;
}

/**
 * TODO(dexterity-physics): the physical rulebook's Wrath sub-rule lets the
 * active player knock a die to try to change its result — a die that falls
 * off the table scores as a negative, and dice landing in an ambiguous spot
 * ("collapsed" against another die or a card) get rerolled individually
 * before scoring (see docs/wireframes/carnival-of-sins/rule.html §6 Wrath /
 * §7). None of that has a digital equivalent yet, so Wrath here is just
 * "pick a die + reroll it" with no negative-score path. A real knock/collapse
 * interaction (client-side physics or a timing-based mini-game) is planned
 * as a later online-specific implementation, not blocking this phase.
 */
export function resolveWrath(
  rngState: CarnivalRngState,
  dice: readonly Die[],
  target: WrathTarget,
): ResolveSinResult {
  const result = rerollDie(rngState, dice, target.dieId);
  return { rngState: result.rngState, dice: result.dice };
}

export function resolveSloth(
  dice: readonly Die[],
  playerId: string,
  seatingOrder: readonly string[],
): readonly Die[] {
  const nearest = nearestUnlockedDie(
    getUnlockedBlackDice(dice),
    playerId,
    seatingOrder,
  );
  return nearest ? lockDice(dice, [nearest.id], playerId, "sloth") : dice;
}

/**
 * Resolves every sin except Pride (deferred to end-of-round, see
 * round-cascade.ts) and Envy (no dice change — its score is derived at
 * scoring time from the devil card holder's dice sum).
 */
export function resolveSinChoice(
  rngState: CarnivalRngState,
  dice: readonly Die[],
  playerId: string,
  seatingOrder: readonly string[],
  choice: SinChoice,
): ResolveSinResult {
  switch (choice.sin) {
    case "greed":
      return { rngState, dice: resolveGreed(dice, playerId, choice.dieIds) };
    case "gluttony":
      return { rngState, dice: resolveGluttony(dice, playerId, choice.dieId) };
    case "envy":
      return { rngState, dice };
    case "lust":
      return { rngState, dice: resolveLust(dice, playerId, choice.picks) };
    case "pride":
      return { rngState, dice };
    case "wrath":
      return resolveWrath(rngState, dice, choice.target);
    case "sloth":
      return { rngState, dice: resolveSloth(dice, playerId, seatingOrder) };
  }
}
